import { observer } from 'mobx-react-lite'
import { useFeatures } from '@/contexts/root-store-context'
import { type DocumentRootViewModel } from '@/features/documents/DocumentRootViewModel'
import { useActivation } from '@/hooks/view-model-hooks'
import { useDocumentTitle } from '@/hooks/document-title'
import { ContentLoading } from '@/components/ContentLoading'
import { ContentError } from '@/components/ContentError'
import { DocumentRoot } from '@/features/documents/components/DocumentRoot'
import { DocumentsListContainer } from '@/features/documents/components/DocumentRoot/DocumentsList/DocumentsListContainer'
import { DocumentsEmptyState } from './DocumentsEmptyState'
import { DocumentsViewHeadingContainer } from './DocumentsViewHeadingContainer'
import { UploadBarContainer } from '@/features/documents/components/DocumentRoot/UploadBar/UploadBarContainer'

export default observer(function DocumentRootContainer() {
  const {
    internal: { documentsVm: vm },
    shellInfo,
  } = useFeatures((r) => ({
    internal: r.documents.internal(),
    shellInfo: r.shell.shellInfo,
  }))

  useDocumentTitle('Documents Center')
  useActivation(vm)

  return (
    <DocumentRoot
      heading={<DocumentsViewHeadingContainer vm={vm} />}
      documentsCount={vm.documentsCount}
      content={vm.fetch.match({
        pending: () =>
          vm.isRevalidating ? (
            <DocumentRootContentContainer vm={vm} />
          ) : (
            <ContentLoading />
          ),
        rejected: (err) => <ContentError error={err} />,
        resolved: () => <DocumentRootContentContainer vm={vm} />,
      })}
      canUploadFiles={vm.canUploadFiles}
      isWorkspaceSelected={vm.isWorkspaceSelected}
      currentWorkspaceName={vm.currentWorkspace.name}
      onQueueFilesUpload={vm.queueFilesUpload}
      onSelectCurrentWorkspace={vm.selectCurrentWorkspaceForFiltering}
      onDragEnter={vm.dragEnter}
      onDragLeave={vm.uploadBarVm.dragLeave}
      onDrop={vm.queueFilesUpload}
      corporateMode={shellInfo.corporateMode}
    />
  )
})

interface DocumentRootContentContainerProps {
  vm: DocumentRootViewModel
}

const DocumentRootContentContainer = observer(
  function DocumentRootContentContainer({
    vm,
  }: DocumentRootContentContainerProps) {
    const { shellInfo } = useFeatures(({ shell }) => ({
      shellInfo: shell.shellInfo,
    }))
    return (
      <>
        {vm.hasDocuments ? (
          <DocumentsListContainer
            vm={vm.documentsListVm}
            canUploadFiles={vm.canUploadFilesToWorkspace}
            onQueueFilesUpload={vm.queueFilesUpload}
            scrollProvider={vm.scrollProvider}
          />
        ) : (
          <DocumentsEmptyState
            canUploadFiles={vm.canUploadFiles}
            onQueueFilesUpload={vm.queueFilesUpload}
            currentWorkspaceEmpty={vm.isWorkspaceEmpty}
            currentWorkspaceName={vm.currentWorkspace.name}
            onSelectCurrentWorkspace={vm.selectCurrentWorkspaceForFiltering}
            corporateMode={shellInfo.corporateMode}
          />
        )}
        {vm.workspaceForFiltering && (
          <UploadBarContainer
            targetWorkspace={vm.workspaceForFiltering}
            vm={vm.uploadBarVm}
          />
        )}
      </>
    )
  },
)
